import { useCallback, useEffect, useState } from 'react';

/**
 * Chrome only fires beforeinstallprompt when the site is installable
 * (HTTPS + valid manifest + controlling service worker).
 */
export function usePwaInstall() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );
  });
  const [swReady, setSwReady] = useState(false);
  const [swError, setSwError] = useState('');

  useEffect(() => {
    const onPrompt = (e) => {
      // Do not preventDefault — that hides Chrome's address-bar install icon.
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      setSwError('This browser does not support service workers.');
      return;
    }
    let cancelled = false;
    const check = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration('/');
        const ready = !!(reg?.active || reg?.waiting || reg?.installing || navigator.serviceWorker.controller);
        if (!cancelled) {
          setSwReady(ready);
          if (!ready) {
            setSwError(
              'Service worker not active. Hard-refresh (Ctrl+Shift+R) after HTTPS is trusted, then wait a few seconds.'
            );
          } else {
            setSwError('');
          }
        }
      } catch (e) {
        if (!cancelled) setSwError(e?.message || 'Service worker check failed');
      }
    };
    check();
    const id = window.setInterval(check, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return { ok: false, reason: 'unavailable' };
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return { ok: choice.outcome === 'accepted', reason: choice.outcome };
  }, [deferred]);

  return {
    canInstall: !!deferred && !installed,
    installed,
    swReady,
    swError,
    promptInstall,
  };
}
